<template>
	<CDialog
		title="Copy Form"
		:visible="isVisible"
		:is-loading="isInitialLoad"
		custom-class="dialog--narrow"
		@closed="$emit('closed')"
	>
		<CCopyForm
			v-if="!isInitialLoad"
			:form-id="formId"
			:default-new-title="newTitle"
			:organizations="organizationOptions"
			:folders="folderOptions"
			:form-copy-info="formCopyInfo"
			:submitter-tracking-decision="submitterTrackingDecision"
			:is-loading="isLoading"
			:is-folder-loading="isFolderLoading"
			:is-copying="isCopying"
			:initial-organization-id="initialOrganizationId"
			:show-unsaved-changes-warning="hasUnsavedChanges && isCurrentForm"
			:is-global-admin="isGlobalAdmin"
			:default-folder="defaultFolder"
			@select-organization="handleSelectOrganization"
			@select-folder="handleSelectFolder"
			@copy="handleCopy"
			@cancel="isVisible = false"
		/>
	</CDialog>
</template>

<script lang="ts">
	import { computed, defineComponent, ref, watch } from 'vue';
	import CDialog from 'src/components/dialogs/Dialog.vue';
	import CCopyForm from 'src/components/app/CopyForm.vue';
	import { globalStore } from 'src/stores/global-store';
	import { getRelatedForms, getSubmitterTrackingDecision } from '@cognitoforms/api/services/form-service';
	import { getAdminOrganizations } from '@cognitoforms/api/services/settings/account-service';
	import { getFolders } from '@cognitoforms/api/services/organization-service';
	import { useCopyFormUtils } from 'src/composables/use-copy-form-utils';
	import type { Folder } from '@cognitoforms/api/services/organization-service';
	import type { FormCopyInfo } from '@cognitoforms/types/server-types/forms/model/form-copy-info';
	import type { SubmitterTrackingDecision } from '@cognitoforms/types/server-types/forms/services/submitter-tracking-decision';
	import type { UserFormMeta } from '@cognitoforms/types/server-types/core/services/user-form-meta';

	export default defineComponent({
		components: {
			CDialog,
			CCopyForm
		},
		inheritAttrs: false,
		props: {
			formId: {
				type: String,
				required: true
			},
			show: {
				type: Boolean,
				default: false
			},
			isCurrentForm: {
				type: Boolean,
				default: false
			}
		},
		emits: ['closed'],
		setup(props) {
			const isInitialLoad = ref(true);
			const isLoading = ref(true);
			const isFolderLoading = ref(true);
			const isCopying = ref(false);
			const formCopyInfo = ref<FormCopyInfo[]>([]);
			const folders = ref<Folder[]>([]);
			const adminOrgs = ref<string[]>([]);
			const formMeta = computed(() => globalStore.forms[props.formId] as UserFormMeta);
			const newTitle = ref('');
			const defaultFolder = ref(null);
			const selectedOrgId = ref(globalStore.organization.Id);
			const hasUnsavedChanges = ref(false);

			const {
				isGlobalAdmin,
				organizationOptions,
				folderOptions,
				checkFormName,
				checkForUnsavedChanges,
				getDefaultFolder,
				handleFormCopy
			} = useCopyFormUtils(folders, adminOrgs);

			const submitterTrackingDecision = ref<SubmitterTrackingDecision | null>(null);

			const fetchSubmitterTrackingDecision = async (destinationOrgId: string): Promise<SubmitterTrackingDecision | null> => {
				const copyMode = destinationOrgId === globalStore.organization.Id ? 'WithinOrg' : 'BetweenOrgs';
				try {
					return await getSubmitterTrackingDecision(globalStore.organization.Id, destinationOrgId, props.formId, copyMode);
				}
				catch {
					return null;
				}
			};

			// Handle fetching folders and checking name availability when organization is selected
			let requestCounter = 0;
			const handleSelectOrganization = async (orgId: string) => {
				isLoading.value = true;
				isFolderLoading.value = true;
				const requestId = ++requestCounter;

				const [[foldersResponse, newDefaultFolder, newTitleResponse], newSubmitterTrackingDecision] = await Promise.all([
					getFolders(orgId).then(async folders => {
						const folder = getDefaultFolder(orgId, folders, formMeta.value);
						return [folders, folder, await checkFormName(orgId, folder, formMeta.value.Name)] as const;
					}),
					fetchSubmitterTrackingDecision(orgId)
				]);

				// Prevent race condition if two orgs are selected quickly
				if (requestId !== requestCounter)
					return;

				folders.value = foldersResponse;
				defaultFolder.value = newDefaultFolder;
				newTitle.value = newTitleResponse;
				selectedOrgId.value = orgId;
				submitterTrackingDecision.value = newSubmitterTrackingDecision;
				isLoading.value = false;
				isFolderLoading.value = false;

				return Promise.resolve();
			};

			const handleSelectFolder = async (folderId: string) => {
				isLoading.value = true;
				newTitle.value = await checkFormName(selectedOrgId.value, folderId, formMeta.value.Name);
				isLoading.value = false;
			};

			const isVisible = ref(props.show);
			watch(() => props.show, () => (isVisible.value = props.show));

			const handleCopy = (destinationOrgId: string, folderId: string, formCopyInfo: FormCopyInfo[]) => {
				isCopying.value = true;

				handleFormCopy(destinationOrgId, folderId, formCopyInfo, submitterTrackingDecision.value).then(() => {
					isVisible.value = false;
					isCopying.value = false;
				}).catch(() => {
					isCopying.value = false;
				});
			};

			const initialize = () => {
				if (!props.show)
					return;

				hasUnsavedChanges.value = checkForUnsavedChanges();

				Promise.all([
					getRelatedForms(globalStore.organization.Code, formMeta.value.UrlName, false),
					getAdminOrganizations()
				]).then(([relatedForms, orgs]) => {
					formCopyInfo.value = relatedForms;
					adminOrgs.value = orgs;
					handleSelectOrganization(globalStore.organization.Id).then(() => {
						isInitialLoad.value = false;
					});
				});
			};

			watch(() => props.show, () => initialize(), { immediate: true });

			return {
				newTitle,
				folderOptions,
				organizationOptions,
				formCopyInfo,
				submitterTrackingDecision,
				isLoading,
				isFolderLoading,
				isCopying,
				isVisible,
				isInitialLoad,
				initialOrganizationId: globalStore.organization.Id,
				handleSelectOrganization,
				handleSelectFolder,
				handleCopy,
				hasUnsavedChanges,
				isGlobalAdmin,
				defaultFolder
			};
		}
	});
</script>

<style lang="scss" src="@cognitoforms/styles/app/form-layout.scss"></style>